import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, ButtonGroup, Drawer, StatusChip, Text, Tooltip } from '@fragments';
import { IconExternalLink, IconRefresh, IconUsers } from '@tabler/icons-react';
import { studioApi } from '../../../../../data/studioApi';
import { SEQUENCE_ICONS } from '../../../../../enums/automations';
import { STUDIO_INBOX } from '../../../../../enums/routes';
import SocialAvatar from '../../../../../components/SocialAvatar';
import PageState from '../../../components/PageState';
import { formatRelative } from '../../../utils/format';

const PER_PAGE = 20;
const FILTERS = { all: {}, clicked: { clicked: true }, email: { has_email: true } };
const CHIPS = { completed: StatusChip.Success, failed: StatusChip.Error, partial: StatusChip.Alert, awaiting_reply: StatusChip.Info, pending: StatusChip.Info, running: StatusChip.Info };

// Who went through it and how far (GET /automations/{id}/executions): each row is one passage,
// with what the person wrote and a mark per step of the sequence.
export default function Executions({ studio: s, automationId, filter: initial, onClose }) {
  const { t } = useTranslation('studioAutomations');
  const [filter, setFilter] = useState(initial);
  const [rows, setRows] = useState(null);
  const [next, setNext] = useState(null);
  const [total, setTotal] = useState(0);
  const [failed, setFailed] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const reads = useRef(0);

  const load = useCallback(async (page = 1) => {
    const read = ++reads.current;
    if (page === 1) { setRows(null); setFailed(false); } else setLoadingMore(true);
    try {
      const answer = await studioApi.automationExecutions(automationId, { ...FILTERS[filter], page, per_page: PER_PAGE });
      if (read !== reads.current) return;
      setRows(current => (page === 1 ? answer.data : [...(current || []), ...answer.data]));
      setTotal(answer.total);
      setNext(answer.current_page < answer.last_page ? answer.current_page + 1 : null);
    } catch { if (read === reads.current) setFailed(true); } finally { if (read === reads.current) setLoadingMore(false); }
  }, [automationId, filter]);
  useEffect(() => { load(); }, [load]);

  return <Drawer open onClose={onClose} width={560} eyebrow={t('executionsEyebrow')} title={rows ? t('executionsTitle', { count: total }) : t('executionsEyebrow')} className="studio-automation-executions">
    <div className="studio-automation-executions__filters">
      <ButtonGroup size="small" value={filter} onChange={setFilter} options={[{ value: 'all', label: t('executionsAll') }, { value: 'clicked', label: t('executionsClicked') }, { value: 'email', label: t('executionsEmail') }]} />
    </div>
    {failed ? <PageState compact text={t('loadError')} action={<Button variant="rounded" color="overlay" startIcon={<IconRefresh />} preventLoading onClick={() => load()}>{t('studio:retry')}</Button>} />
      : !rows ? <ul className="studio-automation-executions__list" aria-busy="true">{[0, 1, 2, 3].map(key => <li key={key}><Text.label loading>{t('executionsEyebrow')}</Text.label></li>)}</ul>
      : !rows.length ? <PageState compact icon={<IconUsers />} text={t(`executionsEmpty_${filter}`)} />
      : <ul className="studio-automation-executions__list">
        {rows.map(row => {
          const Chip = CHIPS[row.status] || StatusChip.Default;
          const failure = row.steps.find(step => step.error)?.error;
          return <li key={row.id}>
            <SocialAvatar className="studio-automation-executions__avatar" picture={row.contact.picture} label={row.contact.handle || row.contact.name} />
            <div className="studio-automation-executions__who">
              <span className="studio-automation-executions__line">
                <Text.label contrast={9} maxLines={1}>{row.contact.handle ? `@${row.contact.handle}` : row.contact.name}</Text.label>
                <Text.caption contrast={6}>{formatRelative(row.timestamps.triggered, s.locale)}</Text.caption>
              </span>
              {row.text && <Text.span contrast={7} maxLines={2}>{row.text}</Text.span>}
              {row.contact.email && <Text.caption contrast={6}>{row.contact.email}</Text.caption>}
              {failure && <Text.caption contrast={8}>{failure.message}</Text.caption>}
              <span className="studio-automation-executions__line">
                <span className="studio-automation-executions__steps">
                  {row.steps.map(step => {
                    const Glyph = SEQUENCE_ICONS[step.step];
                    return <Tooltip key={step.step} content={`${t(`sequence_${step.step}`)}: ${t(`stepStatus_${step.status}`)}`}>
                      <span className={`studio-automation-executions__step is-${step.status}`} aria-label={`${t(`sequence_${step.step}`)}: ${t(`stepStatus_${step.status}`)}`} role="img"><Glyph /></span>
                    </Tooltip>;
                  })}
                </span>
                <Chip text={t(`executionStatus_${row.status}`)} preserveCase />
              </span>
            </div>
          </li>;
        })}
      </ul>}
    {next && <div className="studio-automation-executions__more"><Button variant="rounded" color="overlay" loading={loadingMore} preventLoading onClick={() => load(next)}>{t('mediaLoadMore')}</Button></div>}
    <div className="studio-automation-executions__foot">
      <Button variant="text" color="overlay" size="small" endIcon={<IconExternalLink />} preventLoading onClick={() => s.navigate(STUDIO_INBOX)}>{t('executionsInbox')}</Button>
    </div>
  </Drawer>;
}
